/**
 * ReevaluatePanel — H-215 + H-214.
 *
 * Bottom-sheet style modal for re-ranking a trip's places for a new
 * traveller context. Used in two places:
 *   1. SharedTripPage — anonymous viewers reevaluating a shared trip.
 *   2. SavedTripsScreen detail view — logged-in users reevaluating their
 *      own saved trip (H-214).
 *
 * Three inputs:
 *   - Location: Original (POI centroid) | Current (browser geolocation)
 *   - Transport: walking | transit | taxi
 *   - Time available: 1-2-hrs | half-day | full-day | 2-3-days
 *
 * Submit calls reevaluateTrip(trip, context) and hands the result back to
 * the parent via onReevaluated, which replaces the rendered list in-place.
 */

import { useState } from 'react';
import { X, MapPin, Loader2 } from 'lucide-react';
import type { SavedItinerary } from '@/hooks/useItineraries';
import {
  reevaluateTrip,
  centroidOf,
  type ReevaluateContext,
  type ReevaluatedPlace,
} from '@/services/reevaluate';
import { validateLocation } from '@/services/googlePlaces';
import type { Translations, Language } from '@/translations';

interface ReevaluatePanelProps {
  trip: SavedItinerary;
  language: Language;
  t: Translations;
  onClose: () => void;
  onReevaluated: (results: ReevaluatedPlace[], context: ReevaluateContext) => void;
  /** When the user picks a flag in the modal's lang row, the parent
   *  switches the page's active language so the modal re-renders with
   *  fresh `t`/`language` props and the subsequent reevaluate call uses
   *  the picked language. Lifted to the parent so the rest of the page
   *  stays consistent. */
  onLanguageChange: (lang: Language) => void;
  /** When opening the panel a second time after a previous reevaluation,
   *  pre-fill the inputs from the user's last context instead of resetting
   *  to trip defaults. Lets the user adjust from where they were. */
  previousContext?: ReevaluateContext | null;
  /** Show the language picker row in the header. Default true (used by
   *  SharedTripPage — anon viewer might speak a different language to the
   *  trip's author). False for the in-app saved-trip flow where the
   *  logged-in user's language is already set globally. */
  showLanguagePicker?: boolean;
}

type LocationMode = 'original' | 'current';

const LANG_FLAGS: { lang: Language; emoji: string; label: string }[] = [
  { lang: 'en', emoji: '🇬🇧', label: 'English' },
  { lang: 'fr', emoji: '🇫🇷', label: 'Français' },
  { lang: 'es', emoji: '🇪🇸', label: 'Español' },
  { lang: 'de', emoji: '🇩🇪', label: 'Deutsch' },
];

export function ReevaluatePanel({
  trip,
  language,
  t,
  onClose,
  onReevaluated,
  onLanguageChange,
  previousContext,
  showLanguagePicker = true,
}: ReevaluatePanelProps) {
  const [locationMode, setLocationMode] = useState<LocationMode>(previousContext?.locationMode || 'original');
  const [transport, setTransport] = useState<string>(previousContext?.transport || trip.transport || 'walking');
  const [timeAvailable, setTimeAvailable] = useState<string>(previousContext?.timeAvailable || trip.time_available || 'half-day');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const transportOptions = [
    { value: 'walking', emoji: '🚶', label: t.transport.walking },
    { value: 'transit', emoji: '🚇', label: t.transport.transit },
    { value: 'taxi', emoji: '🚕', label: t.transport.taxi },
  ];

  const timeOptions = [
    { value: '1-2-hrs', label: t.time['1-2-hrs'] },
    { value: 'half-day', label: t.time['half-day'] },
    { value: 'full-day', label: t.time['full-day'] },
    { value: '2-3-days', label: t.time['2-3-days'] },
  ];

  async function resolveOrigin(): Promise<
    { coords: { lat: number; lng: number }; label: string } | null
  > {
    if (locationMode === 'original') {
      // Prefer geocoding the trip's location text — that's the point the
      // ORIGINAL trip's distance_note values were computed against (Google
      // Places search anchor). centroidOf(places) is a geometric average of
      // POIs and drifts far from any individual place on wide-area trips,
      // which inflated travel times by 3× or more in stage testing.
      try {
        const v = await validateLocation(trip.location);
        if (v && v.coords && Number.isFinite(v.coords.lat) && Number.isFinite(v.coords.lng)) {
          return { coords: v.coords, label: trip.location };
        }
      } catch { /* fall through to centroid */ }
      const c = centroidOf(trip.places);
      if (!c) {
        setError(t.reevaluate?.noCoordinatesError || 'This trip has no location data — try Current location instead.');
        return null;
      }
      return { coords: c, label: trip.location };
    }
    // Current — geolocation
    if (!('geolocation' in navigator)) {
      setError(t.reevaluate?.geoUnsupportedError || 'Your browser does not support location services.');
      return null;
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          label: t.reevaluate?.currentLocationLabel || 'Your current location',
        }),
        (err) => {
          setError(
            err.code === err.PERMISSION_DENIED
              ? (t.reevaluate?.geoPermissionError || 'Location permission denied. Pick Original instead.')
              : (t.reevaluate?.geoFailedError || 'Could not get your location. Pick Original instead.')
          );
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
      );
    });
  }

  async function handleSubmit() {
    setError(null);
    setBusy(true);
    try {
      const origin = await resolveOrigin();
      if (!origin) {
        setBusy(false);
        return;
      }
      const context: ReevaluateContext = {
        locationMode,
        originCoords: origin.coords,
        locationLabel: origin.label,
        transport,
        timeAvailable,
        language,
      };
      const results = await reevaluateTrip(trip, context);
      onReevaluated(results, context);
    } catch (err) {
      console.error('[ReevaluatePanel] reevaluate failed:', err);
      setError(t.reevaluate?.genericError || 'Reevaluation failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-[var(--bg-primary)] w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-[var(--border)] max-h-[90vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-[var(--border)] sticky top-0 bg-[var(--bg-primary)] space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
              {t.reevaluate?.title || 'Reevaluate these places'}
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)]"
              aria-label={t.common.close}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Lang picker — clicking a flag immediately re-renders this
              modal in the picked language and updates the page beneath.
              Hidden in the in-app saved-trip flow (user's language is
              already set globally); shown for the anon SharedTripPage flow
              where a French viewer may be reading an English author's
              shared trip. */}
          {showLanguagePicker && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-[var(--text-secondary)] flex-shrink-0">
                {t.reevaluate?.langLabel || 'Lang:'}
              </span>
              <div className="flex items-center gap-1">
                {LANG_FLAGS.map(({ lang, emoji, label }) => (
                  <button
                    key={lang}
                    onClick={() => onLanguageChange(lang)}
                    className={`w-7 h-7 rounded-md flex items-center justify-center text-base transition-all ${
                      language === lang
                        ? 'bg-[var(--accent-teal)]/15 ring-1 ring-[var(--accent-teal)]/40 scale-110'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    aria-label={label}
                    aria-pressed={language === lang}
                    title={label}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-5 space-y-5">
          <p className="text-sm text-[var(--text-secondary)]">
            {t.reevaluate?.subtitle || 'Re-rank these places for your context — where you are now, how you’re getting around, and how long you have.'}
          </p>

          {/* Location mode */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              {t.reevaluate?.locationLabel || 'Location'}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setLocationMode('original')}
                aria-pressed={locationMode === 'original'}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                  locationMode === 'original'
                    ? 'bg-[var(--accent-gold)] text-[var(--bg-primary)]'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                }`}
              >
                <MapPin className="w-4 h-4" />
                {t.reevaluate?.locationOriginal || 'Original'}
              </button>
              <button
                onClick={() => setLocationMode('current')}
                aria-pressed={locationMode === 'current'}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                  locationMode === 'current'
                    ? 'bg-[var(--accent-gold)] text-[var(--bg-primary)]'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                }`}
              >
                <MapPin className="w-4 h-4" />
                {t.reevaluate?.locationCurrent || 'Current'}
              </button>
            </div>
          </div>

          {/* Transport */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              {t.input.howGettingAround}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {transportOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTransport(option.value)}
                  aria-pressed={transport === option.value}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex flex-col items-center gap-0.5 ${
                    transport === option.value
                      ? 'bg-[var(--accent-gold)] text-[var(--bg-primary)]'
                      : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                  }`}
                >
                  <span className="text-lg">{option.emoji}</span>
                  <span className="text-xs">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Time available */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              {t.input.howLong}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {timeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTimeAvailable(option.value)}
                  aria-pressed={timeAvailable === option.value}
                  className={`px-2 py-2 rounded-lg text-xs font-medium transition-all text-center ${
                    timeAvailable === option.value
                      ? 'bg-[var(--accent-gold)] text-[var(--bg-primary)]'
                      : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg p-3">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black font-semibold disabled:opacity-60 flex items-center justify-center gap-2 transition-all hover:shadow-lg"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t.reevaluate?.reevaluating || 'Reevaluating…'}
              </>
            ) : (
              t.reevaluate?.submitButton || 'Reevaluate'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
